import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';
import { Minus, Plus, Sparkles } from 'lucide-react';

/**
 * PotVoteModal — lets a subscriber contribute slices to the Community Fund
 * as votes for a specific campaign or nominated work. Pass exactly one of
 * `campaign` / `work` (with the matching `vote` / `voteWork` callback).
 *
 * Props:
 *   isOpen             — boolean
 *   onClose            — () => void
 *   campaign           — { id, title } — the campaign being voted for
 *   work               — { id, title } — the nominated work being voted for
 *   balance            — user's current slice balance (integer)
 *   isActiveSubscriber — boolean
 *   isAuthenticated    — boolean
 *   vote               — (campaignId, amount) => Promise  from useSlicePot
 *   voteWork           — (workId, amount) => Promise  from useSlicePot
 *   onSuccess          — (result) => void — called with RPC result on success
 */
const PotVoteModal = ({
  isOpen,
  onClose,
  campaign,
  work,
  balance,
  isActiveSubscriber,
  isAuthenticated,
  vote,
  voteWork,
  onSuccess,
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [amount, setAmount] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const safeBalance = balance ?? 0;

  useEffect(() => {
    if (isOpen) setAmount(Math.min(1, safeBalance));
  }, [isOpen, safeBalance]);

  const clamped = safeBalance > 0 ? Math.max(1, Math.min(amount, safeBalance)) : 0;

  const setAmountSafe = (val) => {
    const n = parseInt(val, 10);
    if (!isNaN(n)) setAmount(Math.max(1, Math.min(n, safeBalance)));
  };

  const target = work ?? campaign;

  const handleVote = async () => {
    if (!target || clamped < 1) return;
    setIsLoading(true);
    try {
      const result = work
        ? await voteWork(work.id, clamped)
        : await vote(campaign.id, clamped);
      toast({
        title: `🍕 ${clamped} slice${clamped !== 1 ? 's' : ''} added to the Community Fund!`,
        description: result?.pot_balance
          ? `You're backing "${target.title}". Fund total: 🍕 ${result.pot_balance.toLocaleString()}`
          : `You're backing "${target.title}". Thank you!`,
      });
      onSuccess?.(result);
      onClose();
    } catch (err) {
      toast({
        title: 'Vote failed',
        description: err.message || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  /* ── Signed-out gate ── */
  if (!isAuthenticated) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Sign in to vote</DialogTitle>
            <DialogDescription>
              Join Kinora to direct the Community Fund toward the projects you believe in.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                onClose();
                navigate('/auth');
              }}
            >
              Sign in
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  /* ── No subscription gate ── */
  if (!isActiveSubscriber) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>🍕</span> Subscribe to Vote
            </DialogTitle>
            <DialogDescription>
              Community Fund voting is part of every Kinora subscription. Your unused
              monthly slices roll into the pot automatically — subscribe to take part.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                onClose();
                navigate('/pricing');
              }}
            >
              See plans
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  /* ── Zero balance gate ── */
  if (safeBalance === 0) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>🍕</span> No Slices Available
            </DialogTitle>
            <DialogDescription>
              You've used all your slices for this month. Your balance resets on your
              next billing date — and any unused ones roll straight into the Community
              Fund automatically.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                onClose();
                navigate('/pricing');
              }}
            >
              View plans
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const QUICK_AMOUNTS = [1, 3, 5];

  /* ── Main vote UI ── */
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-400" />
            Vote with Your Slices
          </DialogTitle>
          <DialogDescription>
            Directing the Community Fund toward{' '}
            <span className="font-medium text-foreground">{target?.title}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Balance display */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Available this month</span>
            <span className="font-semibold">🍕 {safeBalance} slices</span>
          </div>

          {/* Amount picker */}
          <div className="space-y-3">
            <p className="text-sm font-medium">How many slices to contribute?</p>

            {/* Quick-amount chips */}
            <div className="flex gap-2 flex-wrap">
              {QUICK_AMOUNTS.map((q) => (
                <Button
                  key={q}
                  type="button"
                  variant={clamped === q ? 'default' : 'outline'}
                  size="sm"
                  className="text-xs h-8 px-4"
                  onClick={() => setAmountSafe(q)}
                  disabled={q > safeBalance}
                >
                  {q}
                </Button>
              ))}
              <Button
                type="button"
                variant={clamped === safeBalance ? 'default' : 'outline'}
                size="sm"
                className="text-xs h-8 px-4"
                onClick={() => setAmountSafe(safeBalance)}
              >
                All {safeBalance}
              </Button>
            </div>

            {/* Slider — only useful when there's range to slide */}
            {safeBalance > 1 && (
              <Slider
                min={1}
                max={safeBalance}
                step={1}
                value={[clamped]}
                onValueChange={([v]) => setAmountSafe(v)}
                className="my-3"
              />
            )}

            {/* Stepper */}
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={() => setAmount((a) => Math.max(1, a - 1))}
                disabled={clamped <= 1}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <input
                type="number"
                min={1}
                max={safeBalance}
                value={clamped}
                onChange={(e) => setAmountSafe(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-center text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={() => setAmount((a) => Math.min(a + 1, safeBalance))}
                disabled={clamped >= safeBalance}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Summary panel */}
          <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 text-sm space-y-1.5">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Contributing to fund</span>
              <span className="font-semibold text-amber-500">🍕 {clamped} slices</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Voting for</span>
              <span className="font-medium truncate ml-4 text-right max-w-[160px]">
                {target?.title}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Your balance after</span>
              <span className="font-medium">🍕 {safeBalance - clamped}</span>
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Your slices join the collective fund. When this voting period closes, the
            entire pot goes to the {work ? 'work' : 'campaign'} the community chose —
            we're funding this together.
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleVote}
            disabled={isLoading}
            className="bg-amber-500 hover:bg-amber-600 text-white border-0"
          >
            {isLoading
              ? 'Voting…'
              : `Vote ${clamped} Slice${clamped !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PotVoteModal;
